import { useMemo, useState } from 'react';
import { useItems } from '@/entities/item/hooks/use-items';
import type { Category } from '@/entities/shared/category/category';
import { compareShopping } from '@/entities/shopping-item/helpers/compare-shopping';
import { useShoppingItems } from '@/entities/shopping-item/hooks/use-shopping-items';
import { rankSuggestions } from '@/shared/lib/text/rank-suggestions';
import { filterShopping } from '../helpers/filter-shopping';
import { storeOptions } from '../helpers/store-options';
import type { ShoppingTab } from '../schemas/shopping-tab';

export function useShoppingList() {
  const list = useShoppingItems();
  const items = useItems();
  const [tab, setTab] = useState<ShoppingTab>('pending');
  const [store, setStore] = useState('');
  const [category, setCategory] = useState<Category | ''>('');
  const stores = useMemo(() => storeOptions(list ?? []), [list]);
  // The stats follow the filter: "what is left at Mercadona and what it costs".
  const filtered = useMemo(
    () => (list ? filterShopping(list, { store, category }) : undefined),
    [list, store, category],
  );
  const shown = useMemo(
    function sortTab() {
      const rows = (filtered ?? []).filter((view) => view.data.status === tab);
      if (tab === 'pending') return rows.sort((a, b) => compareShopping(a.data, b.data));
      return rows.sort((a, b) =>
        (b.data.bought_on ?? b.updated_at).localeCompare(a.data.bought_on ?? a.updated_at),
      );
    },
    [filtered, tab],
  );
  const locations = useMemo(() => rankSuggestions(items ?? [], (item) => item.data.location), [items]);
  return {
    list,
    filtered,
    shown,
    tab,
    setTab,
    locations,
    stores,
    criteria: { store, category },
    setStore,
    setCategory,
    filtering: store !== '' || category !== '',
  };
}
